/**
 * Cookie / tracker consent state.
 *
 * Storage: localStorage under `dp_consent_v1`. The `version` field allows us
 * to bump the policy in code and force every visitor to re-consent — read
 * `readConsent()` and drop the stored object whenever `state.version !==
 * CONSENT_VERSION`.
 *
 * The persisted object is also reflected to Google Consent Mode v2 via
 * `pushGtagConsent`, so any tag that loads later (analytics, ads) starts
 * with the correct grant/deny state without races.
 *
 * Read-after-write coherence: `subscribe(listener)` lets the
 * <ConsentProvider /> reactively re-render when the user updates choices
 * from anywhere (banner, modal, footer button, future settings page).
 */

export type ConsentState = {
  /** Document version. Bump to force-renew consent across the user base. */
  version: string;
  /** ISO timestamp when the user made an explicit choice. `null` = not asked yet. */
  accepted_at: string | null;
  /** Measurement / audience analytics (Google Analytics, Plausible…). */
  analytics: boolean;
  /** Ad serving (Google Ads conversion tags). */
  ads: boolean;
  /** Cross-property personalisation (future). */
  personalization: boolean;
};

export const CONSENT_VERSION = "v1.0.0";
export const STORAGE_KEY = "dp_consent_v1";

const DENIED_STATE: ConsentState = {
  version: CONSENT_VERSION,
  accepted_at: null,
  analytics: false,
  ads: false,
  personalization: false,
};

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

/**
 * Return the current consent state. If no decision has been recorded yet, or
 * if the stored version is stale, return the default-denied state with
 * `accepted_at = null`.
 */
export function readConsent(): ConsentState {
  if (!isBrowser()) return DENIED_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DENIED_STATE;
    const parsed = JSON.parse(raw) as Partial<ConsentState> | null;
    if (!parsed || typeof parsed !== "object") return DENIED_STATE;
    if (parsed.version !== CONSENT_VERSION) {
      // Stale schema — force a fresh consent prompt.
      return DENIED_STATE;
    }
    return {
      version: CONSENT_VERSION,
      accepted_at: parsed.accepted_at ?? null,
      analytics: parsed.analytics === true,
      ads: parsed.ads === true,
      personalization: parsed.personalization === true,
    };
  } catch {
    return DENIED_STATE;
  }
}

/** Persist + broadcast the new state. Idempotent. */
export function writeConsent(next: ConsentState): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* localStorage may be blocked (e.g. iframe sandbox); silent. */
  }
  pushGtagConsent(next);
  for (const listener of listeners) listener(next);
}

/**
 * Push a Google Consent Mode v2 `consent.update` event so any gtag tag
 * loaded by the page picks up the new grants without a page reload.
 *
 * Safe to call multiple times. Safe when `dataLayer` is undefined (we
 * lazily create it).
 */
export function pushGtagConsent(state: ConsentState): void {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push([
    "consent",
    "update",
    {
      ad_storage: state.ads ? "granted" : "denied",
      ad_user_data: state.ads ? "granted" : "denied",
      ad_personalization: state.ads && state.personalization ? "granted" : "denied",
      analytics_storage: state.analytics ? "granted" : "denied",
      functionality_storage: "granted",
      security_storage: "granted",
    },
  ]);
}

/** Convenience: "accept all". */
export function acceptAll(): ConsentState {
  return {
    version: CONSENT_VERSION,
    accepted_at: new Date().toISOString(),
    analytics: true,
    ads: true,
    personalization: true,
  };
}

/** Convenience: "reject all". */
export function rejectAll(): ConsentState {
  return {
    version: CONSENT_VERSION,
    accepted_at: new Date().toISOString(),
    analytics: false,
    ads: false,
    personalization: false,
  };
}

/** Has the visitor made an explicit choice for the current version yet? */
export function hasDecided(state: ConsentState): boolean {
  return state.version === CONSENT_VERSION && state.accepted_at !== null;
}

// ---- subscription -----------------------------------------------------

type Listener = (state: ConsentState) => void;
const listeners = new Set<Listener>();

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
